/**
 * Module dependencies.
 */

import { Button } from 'app/components/atoms/button/button';
import { Drawer } from 'app/components/organisms/drawer/drawer';
import { CrudTemplate } from 'app/components/organisms/crud/form-types';
import { useRequestProvider } from 'app/components/providers/request-provider/request-provider';
import { apiEndpoints } from 'app/core/config/api-endpoints';
import { formatCurrency } from 'app/core/utils/formatter';
import { resolveAppUrl } from 'app/core/utils/url-resolver';
import { useCrudEdit } from 'app/hooks/requests/crud/use-crud-edit';
import type { Transaction } from 'app/types/transaction';
import { Search } from 'lucide-react';
import { Input } from 'app/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from 'app/components/ui/tabs';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import moment, { Moment } from 'moment';

/**
 * Tab keys.
 */

type TabKey = 'similar' | 'reposition';

/**
 * Match highlight style.
 */

const matchStyle: React.CSSProperties = {
  backgroundColor: '#e6f7e6',
  color: '#1a7a1a',
  borderRadius: 2,
  padding: '0 2px'
};

/**
 * Highlight matching substrings in text.
 */

function getBestMatchLength(text: string, source: string): number {
  if (!source || !text) {
    return 0;
  }

  const normalizedText = text.replace(/\s+/g, ' ').trim();
  const words = source.replace(/\s+/g, ' ').trim().split(' ').filter(Boolean);
  const substrings: string[] = [];

  for (let start = 0; start < words.length; start++) {
    for (let end = start + 1; end <= words.length; end++) {
      substrings.push(words.slice(start, end).join(' '));
    }
  }

  let best = 0;

  for (const substring of substrings) {
    if (normalizedText.toLowerCase().includes(substring.toLowerCase()) && substring.length > best) {
      best = substring.length;
    }
  }

  return best;
}

function highlightMatch(text: string, source: string): React.ReactNode {
  if (!source || !text) {
    return text;
  }

  const normalizedText = text.replace(/\s+/g, ' ').trim();
  const words = source.replace(/\s+/g, ' ').trim().split(' ').filter(Boolean);
  const substrings: string[] = [];

  for (let start = 0; start < words.length; start++) {
    for (let end = start + 1; end <= words.length; end++) {
      substrings.push(words.slice(start, end).join(' '));
    }
  }

  let bestMatch = '';

  for (const substring of substrings) {
    if (normalizedText.toLowerCase().includes(substring.toLowerCase()) && substring.length > bestMatch.length) {
      bestMatch = substring;
    }
  }

  if (!bestMatch) {
    return text;
  }

  const index = normalizedText.toLowerCase().indexOf(bestMatch.toLowerCase());
  const before = normalizedText.slice(0, index);
  const match = normalizedText.slice(index, index + bestMatch.length);
  const after = normalizedText.slice(index + bestMatch.length);

  return (
    <>
      {before}
      <span style={matchStyle}>{match}</span>
      {after}
    </>
  );
}

/**
 * `Props` type.
 */

type Props = {
  item: Transaction;
  template: CrudTemplate;
  refetch: () => void;
};

/**
 * Export `SimilarTransactionsAction` component.
 */

export function SimilarTransactionsAction({ item, template, refetch }: Props): JSX.Element {
  const client = useRequestProvider();
  const [isOpen, setOpen] = useState(false);
  const [isLoading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<TabKey>('similar');
  const [similarTransactions, setSimilarTransactions] = useState<Transaction[]>([]);
  const [repositionCandidates, setRepositionCandidates] = useState<Transaction[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [description, setDescription] = useState<string>('');
  const [date, setDate] = useState<Moment | null>(null);
  const { mutateAsync: editTransaction } = useCrudEdit(template, { id: item.id });

  const handleOpen = useCallback(
    (event?: React.MouseEvent<HTMLElement>) => {
      event?.stopPropagation();
      setActiveTab('similar');
      setDescription(item.description ?? '');
      setDate(item.date ? moment(item.date) : null);
      setOpen(true);
    },
    [item.date, item.description]
  );

  const handleClose = useCallback(() => {
    setOpen(false);
    setSelectedId(null);
    setSimilarTransactions([]);
    setRepositionCandidates([]);
  }, []);

  const handleChangeTab = useCallback((key: string) => {
    setActiveTab(key as TabKey);
    setSelectedId(null);
  }, []);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    async function fetchData() {
      setLoading(true);

      try {
        const [similar, reposition] = await Promise.all([
          client.get(resolveAppUrl(apiEndpoints.transactionsSimilar, { interpolations: { id: item.id } })),
          client.get(resolveAppUrl(apiEndpoints.transactionsRepositionCandidates, { interpolations: { id: item.id } }))
        ]);

        setSimilarTransactions(similar.data.data ?? []);
        setRepositionCandidates(reposition.data.data ?? []);
      } catch (error) {
        console.error(error);
      } finally {
        setLoading(false);
      }
    }

    fetchData();
  }, [client, isOpen, item.id]);

  const sortedTransactions = useMemo(() => {
    const source = item.bank_description ?? '';

    return [...similarTransactions].sort((a, b) => {
      const scoreA =
        getBestMatchLength(a.description ?? '', source) +
        getBestMatchLength(a.bank_description ?? '', source) +
        (a.amount === item.amount ? source.length : 0);
      const scoreB =
        getBestMatchLength(b.description ?? '', source) +
        getBestMatchLength(b.bank_description ?? '', source) +
        (b.amount === item.amount ? source.length : 0);

      return scoreB - scoreA;
    });
  }, [item.amount, item.bank_description, similarTransactions]);

  const activeList = activeTab === 'similar' ? sortedTransactions : repositionCandidates;
  const selected = activeList.find(transaction => transaction.id === selectedId);

  const handleApply = useCallback(async () => {
    if (!selected) {
      return;
    }

    const basePayload = {
      description,
      accountId: item.accountId,
      amount: item.amount,
      date: (date ?? moment(item.date)).format('YYYY-MM-DD HH:mm:ss')
    };

    try {
      setLoading(true);

      if (activeTab === 'reposition') {
        await editTransaction({
          ...basePayload,
          transactionType: 'valueReposition',
          targetTransactionId: selected.id
        });
      } else {
        await editTransaction({
          ...basePayload,
          subCategoryId: selected.subCategoryId,
          transactionType: item.transactionType
        });
      }

      refetch();
      handleClose();
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [activeTab, date, description, editTransaction, handleClose, item, refetch, selected]);

  const renderList = (transactions: Transaction[], emptyLabel: string) => {
    if (transactions.length === 0 && !isLoading) {
      return <div style={{ padding: 16, textAlign: 'center', color: '#999' }}>{emptyLabel}</div>;
    }

    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {transactions.map(transaction => {
          const isSelected = transaction.id === selectedId;
          const amountMatches = transaction.amount === item.amount;

          return (
            <div
              key={transaction.id}
              onClick={() => setSelectedId(transaction.id)}
              style={{
                padding: 12,
                border: isSelected ? '2px solid #1890ff' : '1px solid #d9d9d9',
                borderRadius: 6,
                cursor: 'pointer',
                backgroundColor: isSelected ? '#e6f7ff' : '#fff'
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                <span style={{ fontSize: 12, color: '#999' }}>{moment(transaction.date).format('DD-MM-YYYY')}</span>

                <span style={{ fontFamily: 'Oswald', ...(amountMatches ? matchStyle : {}) }}>
                  {formatCurrency(transaction.amount)}
                </span>
              </div>

              {transaction.description && (
                <div style={{ marginBottom: 2 }}>
                  {highlightMatch(transaction.description, item.bank_description ?? '')}
                </div>
              )}

              {transaction.bank_description && (
                <div style={{ fontSize: 11, color: '#999', marginBottom: 4 }}>
                  {highlightMatch(transaction.bank_description, item.bank_description ?? '')}
                </div>
              )}

              {transaction.subCategory && (
                <div style={{ fontSize: 12, color: '#666' }}>
                  {transaction.subCategory.category?.name}
                  {' > '}
                  {transaction.subCategory.name}
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <>
      <Button icon={<Search className={'h-4 w-4'} />} onClick={handleOpen} size={'small'} />

      <Drawer
        buttons={[
          <Button disabled={!selectedId} key={'apply'} loading={isLoading} onClick={handleApply} type={'primary'}>
            {'Aplicar'}
          </Button>
        ]}
        isLoading={isLoading}
        isOpen={isOpen}
        maskClosable={!isLoading}
        onClose={handleClose}
        title={'Transações parecidas'}
        width={600}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <div
            style={{
              padding: 12,
              marginBottom: 4,
              backgroundColor: '#fafafa',
              borderRadius: 6,
              borderLeft: '3px solid #1890ff',
              display: 'flex',
              flexDirection: 'column',
              gap: 8
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Input
                className={'h-8 w-[140px]'}
                onChange={event => event.target.value && setDate(moment(event.target.value, 'YYYY-MM-DD'))}
                type={'date'}
                value={date ? date.format('YYYY-MM-DD') : ''}
              />

              <span style={{ fontFamily: 'Oswald' }}>{formatCurrency(item.amount)}</span>
            </div>

            <Input
              onChange={event => setDescription(event.target.value)}
              placeholder={'Descrição'}
              value={description}
            />

            {item.bank_description && <div style={{ fontSize: 11, color: '#999' }}>{item.bank_description}</div>}
          </div>

          <Tabs onValueChange={handleChangeTab} value={activeTab}>
            <TabsList>
              <TabsTrigger value={'similar'}>{'Similares'}</TabsTrigger>

              <TabsTrigger value={'reposition'}>{'Reposição de valor'}</TabsTrigger>
            </TabsList>

            <TabsContent value={'similar'}>
              {renderList(sortedTransactions, 'Sem transações similares encontradas')}
            </TabsContent>

            <TabsContent value={'reposition'}>
              {renderList(repositionCandidates, 'Sem reposições de valor possíveis')}
            </TabsContent>
          </Tabs>

          {selected && (
            <div style={{ marginTop: 8, padding: 12, backgroundColor: '#f5f5f5', borderRadius: 6 }}>
              <div style={{ fontSize: 12, color: '#999', marginBottom: 4 }}>{'A aplicar:'}</div>

              {activeTab === 'reposition' ? (
                <>
                  <div>{'Reposição de valor'}</div>

                  <div style={{ color: '#666' }}>
                    {moment(selected.date).format('DD-MM-YYYY')}
                    {' — '}
                    {selected.description ?? selected.bank_description}
                    {' — '}
                    {formatCurrency(selected.amount)}
                  </div>
                </>
              ) : (
                <>
                  <div>
                    {selected.subCategory?.category?.name}
                    {' > '}
                    {selected.subCategory?.name}
                  </div>

                  <div style={{ color: '#666' }}>{description}</div>
                </>
              )}
            </div>
          )}
        </div>
      </Drawer>
    </>
  );
}
